import { cn } from '@/utilities/ui'
import { Anchor } from '@/payload-types'
import { CMSLinkType } from '@/types/CMSLink'
import Link from 'next/link'
import React from 'react'
import { Button, type ButtonProps } from './ui/Button'
import { library } from '@fortawesome/fontawesome-svg-core'
import { fab } from '@fortawesome/free-brands-svg-icons'
import { fas } from '@fortawesome/free-solid-svg-icons'
import { Icon } from './Icon'

library.add(fab, fas)

export const CMSLink: React.FC<CMSLinkType<ButtonProps['variant'], ButtonProps['size']>> = (
  props,
) => {
  const {
    type,
    appearance = 'inline',
    children,
    className,
    textClassName,
    iconClassName,
    label,
    newTab,
    reference,
    size: sizeFromProps,
    reactIconsIcon,
    url,
    queryParams,
  } = props

  let href: string | undefined | null = url

  href = getHref()

  if (href === null || href === undefined) return null

  if (queryParams?.length) {
    const params = queryParams
      .map(({ key, value }) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&')
    const [path, hash] = href.split('#')
    const pathWithParams = path + (path?.includes('?') ? `&${params}` : `?${params}`)
    href = hash ? `${pathWithParams}#${hash}` : pathWithParams
  }

  const isAnchor = href.startsWith('#') || href.startsWith('/#')
  const size = appearance === 'link' ? 'clear' : sizeFromProps
  const newTabProps = newTab ? { rel: 'noopener noreferrer', target: '_blank' } : {}

  // Button autour de a et Link = lien ressemble à un bouton graphique, propriété asChild transmet toutes ses propriétés CSS à ses enfants
  // apparence inline = apparence simple, texte standard
  if (isAnchor) {
    if (appearance === 'inline') {
      return (
        <a href={href} className={cn(className)}>
          {label && <span className={textClassName}>{label}</span>}
          {children && children}
          <Icon reactIconsIcon={reactIconsIcon} className={iconClassName} />
        </a>
      )
    } else {
      return (
        <Button asChild className={className} size={size} variant={appearance}>
          <a href={href} className={cn(className)}>
            {label && <span className={textClassName}>{label}</span>}
            {children && children}
            <Icon reactIconsIcon={reactIconsIcon} className={iconClassName} />
          </a>
        </Button>
      )
    }
  } else {
    if (appearance === 'inline') {
      return (
        <Link className={cn(className, 'whitespace-pre-wrap')} href={href} {...newTabProps}>
          {label && <span className={textClassName}>{label}</span>}
          {children && children}
          <Icon reactIconsIcon={reactIconsIcon} className={iconClassName} />
        </Link>
      )
    } else {
      return (
        <Button asChild className={className} size={size} variant={appearance}>
          <Link className={cn(className, 'whitespace-pre-wrap')} href={href} {...newTabProps}>
            {label && <span className={textClassName}>{label}</span>}
            {children && children}
            <Icon reactIconsIcon={reactIconsIcon} className={iconClassName} />
          </Link>
        </Button>
      )
    }
  }

  function getHref() {
    if (type === 'reference' && reference?.value && typeof reference.value === 'object') {
      if (reference.relationTo === 'anchors') {
        // Pour Anchors
        const anchorVal = (reference.value as Anchor).anchor
        return `/#${anchorVal.replace(/^#/, '')}`
      } else if ('slug' in reference.value) {
        // Pour Pages / Posts

        const prefixes: Record<string, string> = {
          pages: '',
          pagesEshop: '/boutique',
          posts: '/posts',
        }

        const basePrefix = prefixes[reference.relationTo] ?? `/${reference.relationTo}`
        const slug = reference.value.slug || ''

        if (reference.relationTo === 'pages' && (slug === '' || slug === null)) {
          return '/'
        }

        return `${basePrefix}/${slug}`.replace(/\/+$/, '') || '/'
      }
    }

    if (type === 'custom' && url) {
      return url
    }
    return null
  }
}
